import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api-client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('cs_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('cs_token'));

  // Refresh user data on mount if token exists
  useEffect(() => {
    if (token && !user) {
      api.getMe().then(data => {
        setUser(data);
        localStorage.setItem('cs_user', JSON.stringify(data));
      }).catch(() => {
        // Token expired
        setToken(null);
        setUser(null);
        localStorage.removeItem('cs_token');
        localStorage.removeItem('cs_user');
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const res = await api.login(email, password);
    setUser(res.user);
    setToken(res.token);
    localStorage.setItem('cs_user', JSON.stringify(res.user));
    localStorage.setItem('cs_token', res.token);
    return res;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.register(data);
    setUser(res.user);
    setToken(res.token);
    localStorage.setItem('cs_user', JSON.stringify(res.user));
    localStorage.setItem('cs_token', res.token);
    return res;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('cs_user');
    localStorage.removeItem('cs_token');
  }, []);

  const refreshUser = useCallback(async () => {
    const data = await api.getMe();
    setUser(data);
    localStorage.setItem('cs_user', JSON.stringify(data));
    return data;
  }, []);

  // Method for OAuth callback - sets tokens and fetches user data
  const setAuthTokens = useCallback(async (accessToken, refreshToken) => {
    setToken(accessToken);
    localStorage.setItem('cs_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('cs_refresh', refreshToken);
    }
    // Fetch user data with the new token
    try {
      const data = await api.getMe();
      setUser(data);
      localStorage.setItem('cs_user', JSON.stringify(data));
    } catch (err) {
      console.error('Failed to fetch user after OAuth:', err);
    }
  }, []);

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, isAuthenticated, refreshUser, setAuthTokens }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
